import React, { useReducer, useEffect } from 'react'
import { getContent, Paths, setContent } from '../firebase/database'
import { getParam } from '../utility'

export interface Dictionary<T> { [Key: string]: T }

interface ISpecKey {
    Name: string,
    Primary: boolean,
    Type: number
}

interface ISpecValues {
    Color: Dictionary<number>,
    String: string
}

interface IStyle {
    InternalName: string,
    SpecValues: Dictionary<ISpecValues>
}

export interface IOffset {
    RotationX: number,
    PositionY: number,
    PositionZ: number,
}

export interface IGlasses {
    id?: string,
    Name: string,
    Subtitle: string,
    SpecKeys: Dictionary<ISpecKey>,
    Styles: Dictionary<IStyle>,
    SizeReferences?: Dictionary<string>,
    DefaultSizeName?: string,
    WebOffset?: IOffset
}

export interface IState {
    glasses: IGlasses,
    otherSizes?: Dictionary<IGlasses>
}

export interface IStore {
    state?: IState,
    dispatch?(action: IAction): void
}

const Store = React.createContext<IStore>({})

// Handle dictionary object changes.
function dictionaryReducer(databasePath: string, dictionary: Dictionary<any>, action: IAction): Dictionary<any> {
    dictionary = dictionary ? { ...dictionary } : {}
    if (!action.payload.id) return dictionary

    if (action.type.includes('set') && action.payload.id) {
        dictionary[action.payload.id] = action.payload

        const firebasePayload = { ...action.payload }
        delete firebasePayload.id

        // HACK
        if (databasePath === Paths.glasses) {
            setContent(`WebOffset/${action.payload.id}`, firebasePayload.WebOffset ?? '')
        }

        // TEMP
        setContent(`${databasePath}/${action.payload.id}`, firebasePayload)
    }

    if (action.type.includes('delete') && action.payload.id) {
        delete dictionary[action.payload.id]
        // setContent(`${databasePath}/${action.payload.id}`, {})
    }

    return dictionary
}

interface IAction {
    type: 'init' | 'set-glasses'
    payload: any
}

function reducer(state: IState, action: IAction) {
    if (action.type === 'init') return action.payload

    if (action.type.includes('glasses')) return { ...state, experiences: dictionaryReducer(Paths.glasses, state.glasses, action) }

    return state
}

export function StoreContext(props) {
    const [state, dispatch] = useReducer(reducer, {})

    async function init(): Promise<void> {
        const modelKey = getParam('model')
        const model = await getContent(`${Paths.glasses}/${modelKey}`) as IGlasses
        let otherSizes = {} as Dictionary<IGlasses>

        // Add other sizes if it has any.
        if (model?.SizeReferences) {
            otherSizes[model.DefaultSizeName] = model
            for (const [key, value] of Object.entries(model.SizeReferences)) {
                const sizeModel = await getContent(`${Paths.glasses}/${value}`) as IGlasses

                if (sizeModel) otherSizes[key] = sizeModel
            }
        }

        const newState: IState = { glasses: model, otherSizes }

        dispatch({ type: 'init', payload: newState })
    }

    useEffect(() => { init() }, [])

    return <Store.Provider value={{ state: state, dispatch: dispatch }}>{props.children}</Store.Provider>
}

export default Store
